import { useState } from 'react';
import { InputNumber } from 'antd';
import { FloatingLabelBox } from '../CustomerSidebar/FloatingLabelBox';
// `.floating-field` sống trong CSS của CustomerSidebar. Import tường minh để
// khối này không phụ thuộc vào việc một component KHÁC tình cờ đã nạp file đó.
import '../CustomerSidebar/CustomerSidebar.css';

export interface PackageInfoValue {
  weight?: number;
  length?: number;
  width?: number;
  height?: number;
}

/** Kiện hàng phổ biến nhất ở quầy — thu ngân chỉ sửa khi khác thường. */
export const DEFAULT_PACKAGE_INFO: Required<PackageInfoValue> = {
  weight: 0.2,
  length: 10,
  width: 10,
  height: 10,
};

interface Props {
  value: PackageInfoValue;
  onChange: (v: PackageInfoValue) => void;
  /** Bật sau khi bấm tạo đơn mà khối lượng chưa hợp lệ. */
  hasError?: boolean;
  /** Chỉ xem — màn sửa đơn hiện đúng khối này cho đơn đã chốt giỏ. */
  disabled?: boolean;
}

// `'Khối lượng (kg)'` rút gọn thành `'KL (kg)'` — đo bằng Playwright cho thấy
// đây là nhãn dài nhất (scrollWidth 100px), buộc CẢ BỐN ô phải rộng ≥132px
// (dùng basis đồng nhất) mới không cắt, mà cột hàng ở chế độ "Giao cho ĐVVC"
// tại 1440px chỉ có 352px content box — không đủ cho 4×132+3×8=552px. Ba nhãn
// còn lại GIỮ NGUYÊN: số đo xác nhận chúng đã vừa chỗ, rút gọn thêm là đánh đổi
// không cần thiết (thu ngân phải đoán đơn vị).
// `ariaLabel` tách khỏi `label` (nhãn NHÌN): rút gọn nhãn nhìn để vừa một hàng
// là quyết định về CHỖ HIỂN THỊ, không nên trả giá bằng tên khả truy cập —
// "KL (kg)" đọc bằng screen reader mất hết nghĩa "khối lượng". Ba ô kích thước
// không rút gọn nên `ariaLabel` trùng `label`, chỉ ô khối lượng khác nhau.
const FIELDS: { key: keyof PackageInfoValue; label: string; ariaLabel: string; step?: number }[] = [
  { key: 'weight', label: 'KL (kg)', ariaLabel: 'Khối lượng (kg)', step: 0.1 },
  { key: 'length', label: 'Dài (cm)', ariaLabel: 'Dài (cm)' },
  { key: 'width', label: 'Rộng (cm)', ariaLabel: 'Rộng (cm)' },
  { key: 'height', label: 'Cao (cm)', ariaLabel: 'Cao (cm)' },
];

/**
 * Bốn ô gói hàng trên MỘT hàng, nhãn nổi như các ô trong dialog khách hàng.
 *
 * Controlled, KHÔNG bind antd Form: bốn field `package_*` đã được
 * `FulfillmentFormSection` đăng ký ẩn — xem docblock `FloatingLabelBox`.
 *
 * `flex-wrap` chứ không media query: khối này nằm ở đáy cột hàng, mà cột đó hẹp
 * đi vì THU NGÂN CHỌN "Giao cho ĐVVC" (cột giữa chiếm chỗ), không phải vì
 * viewport nhỏ. Media query không bao giờ bắt được điều đó.
 */
export function PackageInfoFields({ value, onChange, hasError, disabled }: Props) {
  const [focusedKey, setFocusedKey] = useState<keyof PackageInfoValue | null>(null);

  // GHTK từ chối vận đơn không có khối lượng — đây là guard thật, không phải trang trí.
  const weightInvalid = hasError && !(typeof value.weight === 'number' && value.weight > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Title đứng dòng riêng (block), không nằm trong hàng flex bốn ô — nên
          không ăn vào bề rộng của các ô, bốn ô vẫn xếp một hàng. */}
      <div style={{ fontSize: 12, fontWeight: 700, color: '#333' }}>Thông tin gói hàng</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {FIELDS.map(({ key, label, ariaLabel, step }) => {
          const fieldValue = value[key];
          const isWeightError = key === 'weight' && weightInvalid;
          return (
            <FloatingLabelBox
              key={key}
              label={label}
              isFloating={focusedKey === key || (fieldValue !== undefined && fieldValue !== null)}
              hasError={isWeightError}
              className="floating-field--number pos-package-field"
            >
              <InputNumber
                aria-label={ariaLabel}
                disabled={disabled}
                style={{ width: '100%' }}
                min={0}
                step={step}
                value={fieldValue}
                status={isWeightError ? 'error' : undefined}
                onFocus={() => setFocusedKey(key)}
                onBlur={() => setFocusedKey(null)}
                // Giữ nguyên cả object: nơi gọi ghi thẳng vào Form nên rơi một
                // key là xoá mất kích thước thu ngân vừa nhập ở ô khác.
                onChange={(next) => onChange({ ...value, [key]: next ?? undefined })}
              />
            </FloatingLabelBox>
          );
        })}
      </div>
      {weightInvalid && (
        <div role="alert" style={{ color: '#ff4d4f', fontSize: 11 }}>
          Khối lượng gói hàng phải lớn hơn 0
        </div>
      )}
    </div>
  );
}
